/**
 * Turning stored values into what a person reads.
 *
 * Dates and phone numbers arrive from the API in the shapes a database wants — `2026-08-19`,
 * `201001234567` — and until this file existed they were printed exactly that way. Neither is
 * wrong, and both are unreadable at a glance: a column of ISO dates has to be parsed digit by
 * digit to answer "was that recent?", and an E.164 phone number run together is a number nobody
 * can read back down a telephone.
 *
 * Times and clinic-local days are not here. They live in `routes/scheduling/clinicTime.ts`, which
 * owns the Africa/Cairo conversion (FR-307) — this file only formats values that are already
 * calendar dates, and never converts a zone.
 */

/**
 * `19 Aug 2026`, in the reader's language.
 *
 * Read in UTC deliberately: the input is a calendar date with no time in it, and constructing a
 * `Date` from `2026-08-19` and then formatting it in a local zone west of Greenwich shows the
 * eighteenth. This is the same trap `clinicTime.longDate` documents.
 */
export function shortDate(isoDate: string | null | undefined, locale: string): string {
  if (!isoDate) {
    return '—';
  }
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    return isoDate;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
    // Western digits even in Arabic (ASM-004).
    numberingSystem: 'latn',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * The same date, with the current year left off.
 *
 * For a column where most rows are recent — a patient's last visit, a sale's day. Printing "2026"
 * on every row of a table read in 2026 is four characters that carry no information, and dropping
 * them is what makes the rows that *are* from another year stand out.
 */
export function shortDateSmart(
  isoDate: string | null | undefined,
  locale: string,
  today: Date = new Date(),
): string {
  if (!isoDate) {
    return '—';
  }
  const year = Number(isoDate.slice(0, 4));
  if (year !== today.getUTCFullYear()) {
    return shortDate(isoDate, locale);
  }
  const [, month, day] = isoDate.slice(0, 10).split('-').map(Number);
  if (!month || !day) {
    return isoDate;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    numberingSystem: 'latn',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * An Egyptian mobile number, grouped the way one is written down.
 *
 * The API stores E.164 without the plus (`201001234567`, documented on `PatientSummaryResponse`),
 * and Egyptian mobiles are a 2-digit country code, a 3-digit operator prefix and a 7-digit
 * subscriber number. Anything that is not that shape is returned untouched rather than grouped
 * wrongly — a number a receptionist cannot dial is worse than an ugly one.
 *
 * Always LTR, whatever the interface language: a phone number reversed by the bidi algorithm is a
 * different phone number.
 */
export function phone(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const digits = value.replace(/\D/g, '');
  if (digits.startsWith('20') && digits.length === 12) {
    return `+20 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return value;
}

/**
 * A timestamp as `19 Aug, 14:32`.
 *
 * For audit trails and message threads, where the date alone is too coarse and the full ISO
 * instant is unreadable. Seconds are dropped: nothing in this product is decided by them, and they
 * are three characters of noise on every row.
 */
export function dateTime(instant: string | null | undefined, locale: string): string {
  if (!instant) {
    return '—';
  }
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) {
    return instant;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    numberingSystem: 'latn',
  }).format(parsed);
}

/**
 * A plain integer, grouped: `1,284`.
 *
 * Small enough to be a one-liner at every call site, which is exactly why it kept being written
 * differently at each one — or not at all, which is how the audit log came to say `1284 entries`.
 */
export function count(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-EG').format(value);
}

/**
 * A figure shortened for an axis: `12K`, `1.5M` (CR-34).
 *
 * An axis has room for four characters, and "12,000" beside "6,000" is a column of noise next to
 * a chart that is already saying it. Western digits in both languages (ASM-004), so the Latin
 * compact suffixes are used in Arabic too, as clinic staff read prices.
 */
export function compact(value: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * Money in whole pounds: `EGP 12,345` (CR-34).
 *
 * For charts and their figures, where piastres are noise; a receipt or a table keeps them, through
 * `money()` in routes/catalog/money.ts.
 */
export function pounds(value: number, currency = 'EGP'): string {
  return `${currency} ${new Intl.NumberFormat('en-EG', { maximumFractionDigits: 0 }).format(Math.round(value))}`;
}
